import { CommonModule } from '@angular/common';
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CentruiIconComponent } from '../../../../centrui/src/app/components/centrui-icon/centrui-icon.component';
import { CentruiSpinnerComponent } from '../../../../centrui/src/app/components/centrui-spinner/centrui-spinner.component';
import { AuthService } from '../../../../centrui/src/app/services/auth.service';
import { LumiMode } from '../../../constants/LumiMode';
import { LumiSkill } from '../../../constants/LumiSkill';
import { Project } from '../../../models/Project';
import {
  ConversationMessage,
  extractLumiKbArticles,
  extractLumiReplyAbbreviations,
  extractLumiReplyCitations,
  extractLumiReplyGlossary,
  extractLumiReplyLexicon,
  extractLumiReplyPaths,
  extractLumiStreamAnswerText,
  lexiconPanelRefTermFromReply,
  LumiMessageFeedbackPayload,
  LumiReplyCitation,
  LumiReplyKbArticle,
  LumiReplyPath,
  LumiService,
  normalizeLumiCitationPayload,
  stripPanelRefSearchText,
} from '../../../services/lumi.service';
import { ChatMessagePanelRefSection, ChatMessageSource, LumiChatMessage } from '../lumi-chat-message/lumi-chat-message.model';
import { CentruiTagComponent } from '../../../../centrui/src/app/components/centrui-tag/centrui-tag.component';
import { LumiChatMessageComponent } from '../lumi-chat-message/lumi-chat-message.component';

@Component({
  selector: 'app-lumi-chat',
  imports: [CommonModule, FormsModule, CentruiIconComponent, CentruiSpinnerComponent, CentruiTagComponent, LumiChatMessageComponent],
  templateUrl: './lumi-chat.component.html',
  styleUrl: './lumi-chat.component.scss',
})
export class LumiChatComponent implements OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input({ required: true }) mode!: LumiMode;
  @Input() project?: Project;
  @Input() title?: string;
  @Input() threadId?: string;
  @Input() skill?: LumiSkill;
  @Output() back = new EventEmitter<'home' | 'history'>();
  @Output() threadResolved = new EventEmitter<string>();
  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLElement>;
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('feedbackInput') feedbackInput!: ElementRef<HTMLTextAreaElement>;

  messages: LumiChatMessage[] = [];
  staggerCount = 0;
  isStreaming = false;
  feedbackOverlayVisible = false;
  feedbackThankYouVisible = false;
  feedbackThankYouFadingOut = false;
  feedbackText = '';
  loadingHistory = false;
  loadingOlderMessages = false;
  private pendingScrollToBottom = false;
  private pendingSmoothScroll: { previousScrollHeight: number; messageCount: number } | null = null;
  private currentPage = 1;
  private totalMessages = 0;
  private pageSize = 5;
  private hasMoreMessages = false;
  private smoothScrolling = false;
  private scrollLoadArmed = false;
  private scrollAnimFrame = 0;
  private thankYouTimer?: ReturnType<typeof setTimeout>;
  private thankYouFadeTimer?: ReturnType<typeof setTimeout>;
  private streamSubscription?: Subscription;
  private sessionThreadId?: string;
  private feedbackTargetMessage: LumiChatMessage | null = null;

  constructor(
    public authService: AuthService,
    private lumiService: LumiService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  emitBack(): void {
    this.back.emit('home');
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
    this.focusMessageInput();
  }

  focusMessageInput(): void {
    setTimeout(() => this.messageInput?.nativeElement.focus());
  }

  ngAfterViewChecked(): void {
    if (this.pendingScrollToBottom) {
      this.pendingScrollToBottom = false;
      this.scrollToBottom();
    }

    if (this.pendingSmoothScroll) {
      const { previousScrollHeight, messageCount } = this.pendingSmoothScroll;
      this.pendingSmoothScroll = null;

      const el = this.messagesContainer?.nativeElement;
      if (el) {
        const newContentHeight = el.scrollHeight - previousScrollHeight;
        el.scrollTop = newContentHeight;

        if (newContentHeight > 1) {
          const duration = 150 + (messageCount - 1) * 80 + 200;
          this.smoothScrollTo(el, 0, duration);
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['threadId']) {
      const ch = changes['threadId'];
      const prev = ch.previousValue as string | undefined;
      if (this.threadId) {
        this.sessionThreadId = undefined;
        const shouldHydrateFromApi =
          (ch.firstChange && !!this.threadId) ||
          (prev !== undefined && prev !== this.threadId);
        if (shouldHydrateFromApi && !this.isStreaming) {
          this.loadInitialMessages(this.threadId);
        }
      } else if (!ch.firstChange) {
        this.sessionThreadId = undefined;
      }
    }
    if ((changes['mode'] || changes['project']) && !this.threadId) {
      this.resetPagination();
      this.buildGreeting();
      setTimeout(() => this.scrollToBottom());
    }
  }

  autoGrow(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    const maxHeightPx = parseFloat(getComputedStyle(textarea).maxHeight);
    const contentHeight = textarea.scrollHeight;
    if (!Number.isNaN(maxHeightPx) && contentHeight > maxHeightPx) {
      textarea.style.height = `${maxHeightPx}px`;
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.height = `${contentHeight}px`;
      textarea.style.overflowY = 'hidden';
    }
  }

  onEnterKey(event: Event): void {
    const keyEvent = event as KeyboardEvent;
    if (keyEvent.shiftKey) return;
    if (this.isStreaming) {
      keyEvent.preventDefault();
      return;
    }
    keyEvent.preventDefault();
    this.sendMessage();
  }

  sendMessage(): void {
    const text = this.messageInput?.nativeElement.value.trim();
    if (!text || this.isStreaming) return;

    const userAvatar = this.authService.userObject?.file || 'assets/img/avatar.svg';
    const now = new Date();
    const timestamp = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
      + ', ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    this.messages.push({
      type: 'user',
      content: text,
      avatarUrl: userAvatar,
      timestamp,
    });

    const inputEl = this.messageInput.nativeElement;
    inputEl.value = '';
    inputEl.dispatchEvent(new Event('input', { bubbles: true }));
    setTimeout(() => inputEl.focus());

    const botMessage: LumiChatMessage = {
      type: 'bot',
      content: '',
      avatarUrl: 'assets/img/Lumi-Avatar.svg',
      replyStreaming: true,
    };
    this.messages.push(botMessage);
    const botMessageIndex = this.messages.length - 1;
    setTimeout(() => this.scrollToBottom());

    this.isStreaming = true;

    this.streamLumiReply(this.buildChatStreamPayload(text), botMessageIndex);
  }

  private buildChatStreamPayload(text: string): Record<string, unknown> {
    const payload: Record<string, unknown> = { message: text };
    if (this.mode === LumiMode.PROJECT_HELP) {
      payload['skill'] = this.skill ?? LumiSkill.PROJECT_QA;
      if (this.project) {
        payload['project_id'] = this.project.id;
      }
    } else if (this.mode === LumiMode.POLARIX_HELP) {
      payload['skill'] = this.skill ?? LumiSkill.PLATFORM_QA;
    } else if (this.skill) {
      payload['skill'] = this.skill;
    }
    const threadId = this.threadId ?? this.sessionThreadId;
    if (threadId) {
      payload['thread_id'] = threadId;
    }
    return payload;
  }

  private streamLumiReply(payload: Record<string, unknown>, botMessageIndex: number): void {
    let plainContent = '';
    let lastFinalPayload: unknown;

    this.streamSubscription?.unsubscribe();
    this.streamSubscription = this.lumiService.chatStream(payload).subscribe({
      next: (part) => {
        const botMessage = this.messages[botMessageIndex];
        if (!botMessage) {
          return;
        }
        if (part.kind === 'thread') {
          if (!this.threadId) {
            this.sessionThreadId = part.thread_id;
          }
          return;
        }
        if (part.kind === 'final') {
          const d = part.data;
          lastFinalPayload = d;
          const patch: Partial<LumiChatMessage> = { replyStreaming: false };
          if (d && typeof d === 'object' && !Array.isArray(d)) {
            const rec = d as Record<string, unknown>;
            const tid = rec['thread_id'];
            if (typeof tid === 'string' && tid && !this.threadId) {
              this.sessionThreadId = tid;
              this.threadResolved.emit(tid);
            }
            this.applyCitationPathsFromPayload(botMessage, d);
            this.applyPanelRefsFromPayload(botMessage, d);
            const mid = LumiChatComponent.readMessageIdFromPayload(d);
            if (mid) {
              patch.messageId = mid;
            }
            if (botMessage.sources?.length) {
              patch.sources = [...botMessage.sources];
              patch.sourcesLabel = botMessage.sourcesLabel;
              patch.sourceCitationStyle = botMessage.sourceCitationStyle;
            }
            if (botMessage.panelRefSections?.length) {
              patch.panelRefSections = botMessage.panelRefSections.map((section) => ({
                label: section.label,
                refs: section.refs.map((ref) => ({ ...ref })),
              }));
            }
          }
          const answerText = extractLumiStreamAnswerText(d);
          if (answerText && !plainContent.trim()) {
            plainContent = answerText;
            patch.content = answerText;
          }
          this.patchBotMessage(botMessageIndex, patch);
          setTimeout(() => this.scrollToBottom());
          return;
        }
        if (part.kind !== 'text') {
          return;
        }
        const token = part.text;
        const separator = plainContent ? ' ' : '';
        this.patchBotMessage(botMessageIndex, {
          content: plainContent + `${separator}<span class="lumi-word-fade">${token}</span>`,
        });
        plainContent += separator + token;
        setTimeout(() => this.scrollToBottom());
      },
      error: () => {
        const botMessage = this.messages[botMessageIndex];
        if (!botMessage) {
          return;
        }
        this.patchBotMessage(botMessageIndex, {
          content: plainContent || 'Sorry, something went wrong. Please try again.',
          replyStreaming: false,
        });
        this.clearCitationFooter(botMessage);
        this.clearPanelRefs(botMessage);
        this.isStreaming = false;
        this.changeDetectorRef.detectChanges();
        setTimeout(() => this.messageInput?.nativeElement.focus());
      },
      complete: () => {
        const botMessage = this.messages[botMessageIndex];
        if (!botMessage) {
          return;
        }
        if (!plainContent.trim()) {
          const fallbackAnswer = extractLumiStreamAnswerText(lastFinalPayload);
          if (fallbackAnswer) {
            plainContent = fallbackAnswer;
          }
        }
        this.patchBotMessage(botMessageIndex, {
          replyStreaming: false,
          content: plainContent,
          showFeedback: !!botMessage.messageId,
          timestamp: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
            + ', ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        });
        this.isStreaming = false;
        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
          this.scrollToBottom();
          this.messageInput?.nativeElement.focus();
        });
      },
    });
  }

  private patchBotMessage(index: number, patch: Partial<LumiChatMessage>): void {
    const current = this.messages[index];
    if (!current) {
      return;
    }
    this.messages[index] = { ...current, ...patch };
    this.messages = [...this.messages];
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  private scrollToBottom(): void {
    const el = this.messagesContainer?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }

  private smoothScrollTo(el: HTMLElement, target: number, duration: number): void {
    this.smoothScrolling = true;
    cancelAnimationFrame(this.scrollAnimFrame);

    const start = el.scrollTop;
    const distance = target - start;
    const startTime = performance.now();

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      el.scrollTop = start + distance * easeOutCubic(progress);

      if (progress < 1) {
        this.scrollAnimFrame = requestAnimationFrame(step);
      } else {
        this.smoothScrolling = false;
      }
    };

    this.scrollAnimFrame = requestAnimationFrame(step);
  }

  onMessageUpvote(msg: LumiChatMessage): void {
    if (!msg.messageId) return;
    this.sendFeedback(msg.messageId, { feedback: 'up', project_id: this.project!.id });
    this.showThankYou();
  }

  onMessageDownvote(msg: LumiChatMessage): void {
    if (!msg.messageId) return;
    this.feedbackTargetMessage = msg;
    this.feedbackOverlayVisible = true;
    this.feedbackText = '';
    setTimeout(() => this.feedbackInput?.nativeElement.focus());
  }

  skipFeedback(): void {
    const id = this.feedbackTargetMessage?.messageId;
    this.feedbackOverlayVisible = false;
    this.feedbackText = '';
    this.feedbackTargetMessage = null;
    if (id) {
      this.sendFeedback(id, { feedback: 'down', project_id: this.project!.id });
    }
    this.showThankYou();
  }

  submitFeedback(): void {
    const text = this.feedbackText.trim();
    const id = this.feedbackTargetMessage?.messageId;
    if (!text || !id) return;
    this.sendFeedback(id, { feedback: 'down', reason: text, project_id: this.project!.id });
    this.feedbackOverlayVisible = false;
    this.feedbackText = '';
    this.feedbackTargetMessage = null;
    this.showThankYou();
  }

  private sendFeedback(messageId: string, payload: LumiMessageFeedbackPayload): void {
    this.lumiService.postMessageFeedback(messageId, payload).subscribe({
      error: (err) => console.warn('Lumi message feedback failed', err),
    });
  }

  hideThankYou(): void {
    this.fadeOutThankYou();
  }

  ngOnDestroy(): void {
    this.clearThankYouTimers();
    cancelAnimationFrame(this.scrollAnimFrame);
    this.streamSubscription?.unsubscribe();
    this.lumiService.cancelStream();
  }

  private showThankYou(): void {
    this.feedbackThankYouFadingOut = false;
    this.feedbackThankYouVisible = true;
    setTimeout(() => this.scrollToBottom());
    this.clearThankYouTimers();
    this.thankYouTimer = setTimeout(() => this.fadeOutThankYou(), 10_000);
  }

  private fadeOutThankYou(): void {
    this.clearThankYouTimers();
    this.feedbackThankYouFadingOut = true;
    this.thankYouFadeTimer = setTimeout(() => {
      this.feedbackThankYouVisible = false;
      this.feedbackThankYouFadingOut = false;
    }, 200);
  }

  private clearThankYouTimers(): void {
    if (this.thankYouTimer) {
      clearTimeout(this.thankYouTimer);
      this.thankYouTimer = undefined;
    }
    if (this.thankYouFadeTimer) {
      clearTimeout(this.thankYouFadeTimer);
      this.thankYouFadeTimer = undefined;
    }
  }

  private getTimeOfDayGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }

  getStaggerDelay(index: number): string {
    if (index >= this.staggerCount) return '0ms';
    return (150 + ((this.staggerCount - 1 - index) * 80)) + 'ms';
  }

  onMessagesScroll(): void {
    if (this.loadingHistory || this.loadingOlderMessages || this.smoothScrolling || !this.hasMoreMessages || !this.threadId) return;

    const el = this.messagesContainer?.nativeElement;
    if (!el) return;

    if (el.scrollHeight <= el.clientHeight) {
      return;
    }

    if (el.scrollTop > 30) {
      this.scrollLoadArmed = true;
    }

    if (this.scrollLoadArmed && el.scrollTop <= 30) {
      this.scrollLoadArmed = false;
      this.loadOlderMessages(this.threadId);
    }
  }

  onMessagesWheel(event: WheelEvent): void {
    if (event.deltaY >= 0) return;
    if (this.loadingHistory || this.loadingOlderMessages || this.smoothScrolling || !this.hasMoreMessages || !this.threadId) return;

    const el = this.messagesContainer?.nativeElement;
    if (!el || el.scrollHeight <= el.clientHeight) return;

    if (el.scrollTop <= 0) {
      this.scrollLoadArmed = false;
      this.loadOlderMessages(this.threadId);
    }
  }

  private resetPagination(): void {
    this.currentPage = 1;
    this.totalMessages = 0;
    this.hasMoreMessages = false;
    this.loadingHistory = false;
    this.loadingOlderMessages = false;
    this.scrollLoadArmed = false;
  }

  private normalizeConversationMessagesResponse(response: unknown): ConversationMessage[] {
    if (response && typeof response === 'object') {
      const payload = (response as { data?: unknown }).data ?? response;
      if (payload && typeof payload === 'object') {
        const messages = (payload as { messages?: unknown }).messages;
        if (Array.isArray(messages)) {
          return messages as ConversationMessage[];
        }
      }
    }
    return [];
  }

  private normalizeConversationMessagesPage(response: unknown, fallbackPage: number, fallbackPageSize: number): {
    total: number;
    page: number;
    page_size: number;
  } {
    const payload = response && typeof response === 'object'
      ? ((response as { data?: unknown }).data ?? response)
      : {};
    const record = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
    return {
      total: Number(record['total'] ?? 0),
      page: Number(record['page'] ?? fallbackPage),
      page_size: Number(record['page_size'] ?? fallbackPageSize),
    };
  }

  private loadInitialMessages(threadId: string): void {
    this.resetPagination();
    this.loadingHistory = true;
    this.messages = [];
    this.feedbackOverlayVisible = false;
    this.feedbackThankYouVisible = false;
    this.feedbackThankYouFadingOut = false;
    this.feedbackText = '';
    this.feedbackTargetMessage = null;

    this.lumiService.getConversationMessages(threadId, 1, this.pageSize).subscribe({
      next: (response) => {
        if (this.threadId !== threadId) return;

        const page = this.normalizeConversationMessagesPage(response, 1, this.pageSize);
        this.totalMessages = page.total;
        this.currentPage = page.page;
        this.pageSize = page.page_size;
        this.hasMoreMessages = page.page * page.page_size < page.total;
        this.messages = this.mapApiMessages(this.normalizeConversationMessagesResponse(response)).reverse();
        this.staggerCount = this.messages.length;
        this.loadingHistory = false;
        this.pendingScrollToBottom = true;
        this.changeDetectorRef.markForCheck();
      },
      error: () => {
        if (this.threadId !== threadId) return;
        this.loadingHistory = false;
        this.changeDetectorRef.markForCheck();
      },
    });
  }

  private loadOlderMessages(threadId: string): void {
    this.loadingOlderMessages = true;
    const nextPage = this.currentPage + 1;

    const el = this.messagesContainer?.nativeElement;
    const previousScrollHeight = el?.scrollHeight ?? 0;

    this.lumiService.getConversationMessages(threadId, nextPage, this.pageSize).subscribe({
      next: (response) => {
        if (this.threadId !== threadId) return;

        const page = this.normalizeConversationMessagesPage(response, nextPage, this.pageSize);
        this.currentPage = page.page;
        this.hasMoreMessages = page.page * page.page_size < page.total;
        const olderMessages = this.mapApiMessages(this.normalizeConversationMessagesResponse(response)).reverse();
        this.staggerCount = olderMessages.length;
        this.messages = [...olderMessages, ...this.messages];
        this.loadingOlderMessages = false;
        this.pendingSmoothScroll = { previousScrollHeight, messageCount: olderMessages.length };
        this.changeDetectorRef.markForCheck();
      },
      error: () => {
        if (this.threadId !== threadId) return;
        this.loadingOlderMessages = false;
        this.changeDetectorRef.markForCheck();
      },
    });
  }

  private stripUserInputTags(content?: string | null): string {
    if (!content) return '';
    return content.replace(/<\/?USER_INPUT>/gi, '').trim();
  }

  private mapApiMessages(apiMessages: ConversationMessage[]): LumiChatMessage[] {
    const userAvatar = this.authService.userObject?.file || 'assets/img/avatar.svg';
    return apiMessages.map((msg) => {
      const isUser = msg.role === 'user';
      const chatMsg: LumiChatMessage = {
        type: isUser ? 'user' : 'bot',
        content: isUser ? this.stripUserInputTags(msg.content) : (msg.content || ''),
        avatarUrl: isUser ? userAvatar : 'assets/img/Lumi-Avatar.svg',
      };
      if (!isUser) {
        this.applyCitationPathsFromPayload(chatMsg, msg.metadata);
        this.applyPanelRefsFromPayload(chatMsg, msg.metadata);
        const mid = LumiChatComponent.readMessageIdFromPayload(msg.metadata);
        if (mid) {
          chatMsg.messageId = mid;
          chatMsg.showFeedback = true;
        }
      }
      if (msg.created_at) {
        const date = new Date(msg.created_at);
        chatMsg.timestamp = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
          + ', ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      }
      return chatMsg;
    });
  }

  private clearCitationFooter(message: LumiChatMessage): void {
    if (message.type !== 'bot') return;
    delete message.sources;
    delete message.sourcesLabel;
    delete message.sourceCitationStyle;
  }

  private clearPanelRefs(message: LumiChatMessage): void {
    if (message.type !== 'bot') return;
    delete message.panelRefSections;
  }

  private static readonly KB_ARTICLES_LABEL = 'Knowledge base articles:';
  private static readonly REFERENCES_LABEL = 'References:';
  private static readonly ABBREVIATIONS_LABEL = 'Abbreviations:';
  private static readonly GLOSSARY_LABEL = 'Glossary:';
  private static readonly LEXICON_LABEL = 'Lexicon:';

  private applyPanelRefsFromPayload(message: LumiChatMessage, payload: unknown): void {
    if (message.type !== 'bot') return;
    const sections: ChatMessagePanelRefSection[] = [];
    const abbreviations = extractLumiReplyAbbreviations(payload);
    if (abbreviations.length > 0) {
      sections.push({
        label: LumiChatComponent.ABBREVIATIONS_LABEL,
        refs: abbreviations.map((a) => ({
          label: a.code,
          tabId: 'abbreviations' as const,
          entityId: a.id,
          searchQuery: stripPanelRefSearchText(a.code),
        })),
      });
    }
    const glossary = extractLumiReplyGlossary(payload);
    if (glossary.length > 0) {
      sections.push({
        label: LumiChatComponent.GLOSSARY_LABEL,
        refs: glossary.map((g) => ({
          label: g.term,
          tabId: 'glossary' as const,
          entityId: g.id,
          searchQuery: stripPanelRefSearchText(g.term),
        })),
      });
    }
    const lexicon = extractLumiReplyLexicon(payload);
    if (lexicon.length > 0) {
      sections.push({
        label: LumiChatComponent.LEXICON_LABEL,
        refs: lexicon.map((l) => ({
          label: stripPanelRefSearchText(l.preferred_term) || l.preferred_term,
          tabId: 'lexicon' as const,
          entityId: l.id,
          searchQuery: lexiconPanelRefTermFromReply(l),
        })),
      });
    }
    if (sections.length > 0) {
      message.panelRefSections = sections;
    } else {
      this.clearPanelRefs(message);
    }
  }

  private applyCitationPathsFromPayload(message: LumiChatMessage, payload: unknown): void {
    if (message.type !== 'bot') return;
    const paths = extractLumiReplyPaths(payload);
    if (paths.length > 0) {
      message.sourcesLabel = 'Found in:';
      message.sourceCitationStyle = 'project';
      message.sources = paths.map((p) => LumiChatComponent.pathToChatSource(p));
      return;
    }
    const citations = extractLumiReplyCitations(payload);
    if (citations.length > 0) {
      message.sourcesLabel = LumiChatComponent.REFERENCES_LABEL;
      message.sourceCitationStyle = 'project';
      message.sources = citations.map((c) => LumiChatComponent.citationToChatSource(c));
      return;
    }
    const articles = extractLumiKbArticles(payload);
    if (articles.length > 0) {
      message.sourcesLabel = LumiChatComponent.KB_ARTICLES_LABEL;
      message.sourceCitationStyle = 'project';
      message.sources = articles.map((a) => LumiChatComponent.kbArticleToChatSource(a));
      return;
    }
    this.clearCitationFooter(message);
  }

  private static kbArticleToChatSource(a: LumiReplyKbArticle): ChatMessageSource {
    return { label: a.title, url: a.url };
  }

  private static citationToChatSource(c: LumiReplyCitation): ChatMessageSource {
    const pagePart = c.page != null ? ` · p. ${c.page}` : '';
    return {
      label: `${c.name}${pagePart}`,
      attachmentId: c.attachmentId,
      citationPage: c.page,
    };
  }

  get sourceNavigationProjectId(): number | undefined {
    return this.mode === LumiMode.PROJECT_HELP ? this.project?.id : undefined;
  }

  get sourceNavigationProjectSlug(): string | undefined {
    return this.mode === LumiMode.PROJECT_HELP ? this.project?.slug : undefined;
  }

  private static parseJsonIfString(payload: unknown): unknown {
    if (typeof payload !== 'string') return payload;
    try {
      return JSON.parse(payload) as unknown;
    } catch {
      return undefined;
    }
  }

  private static readMessageIdFromPayload(data: unknown): string | undefined {
    const read = (obj: unknown): string | undefined => {
      if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return undefined;
      const id = (obj as Record<string, unknown>)['message_id'];
      if (id == null) return undefined;
      const s = String(id).trim();
      return s || undefined;
    };
    const fromParsed = read(LumiChatComponent.parseJsonIfString(data));
    if (fromParsed) return fromParsed;
    return read(normalizeLumiCitationPayload(data));
  }

  private static pathToSourceLabel(p: LumiReplyPath): string {
    return [p.section.trim(), p.prefix.trim()].filter(Boolean).join(' ');
  }

  private static pathToChatSource(p: LumiReplyPath): ChatMessageSource {
    const label = LumiChatComponent.pathToSourceLabel(p);
    const src: ChatMessageSource = { label };
    if (p.item_id != null && !Number.isNaN(Number(p.item_id))) {
      src.itemId = typeof p.item_id === 'number' ? p.item_id : Number(p.item_id);
    }
    const firstContent = p.item_content_ids?.[0];
    if (firstContent != null && !Number.isNaN(Number(firstContent))) {
      src.itemContentId = typeof firstContent === 'number' ? firstContent : Number(firstContent);
    }
    return src;
  }

  private buildGreeting(): void {
    const userName = this.authService.userObject?.name?.split(' ')[0] ?? '';
    const greeting = this.getTimeOfDayGreeting();

    if (this.mode === LumiMode.PROJECT_HELP && this.project) {
      this.messages = [
        {
          type: 'bot',
          content: `${greeting} ${userName}, I am here to help, please ask any question about the <strong>${this.project.title}</strong> project`,
          avatarUrl: 'assets/img/Lumi-Avatar.svg',
        },
      ];
    } else {
      this.messages = [
        {
          type: 'bot',
          content: `${greeting} ${userName}, I am here to help, please ask any question about the Polarix.io platform`,
          avatarUrl: 'assets/img/Lumi-Avatar.svg',
        },
      ];
    }

    this.staggerCount = this.messages.length;
    this.feedbackOverlayVisible = false;
    this.feedbackThankYouVisible = false;
    this.feedbackThankYouFadingOut = false;
    this.feedbackText = '';
    this.feedbackTargetMessage = null;
  }
}
